import {
  VStack,
  type ResponsiveValue,
  type StackProps,
} from '@chakra-ui/react';
import { type FC, type RefObject } from 'react';

import { FanPresentationRow } from '$/components/core/Collection/MaterialPresentation/FanPresentationRow';
import { ListPresentationItem } from '$/components/core/Collection/MaterialPresentation/ListPresentationItem';
import {
  type Material,
  type MaterialType,
} from '$/services/usecases/materials/mapper/materials';
import { repeat } from '$/utils/arrayUtils';

interface Props {
  observer: RefObject<HTMLDivElement | null>;
  setActiveMaterial: (material: Material) => void;
  materials: Material[];
  type: MaterialType;
  isLoading: boolean;
  disableHovering?: boolean;
  itemHeight?: ResponsiveValue<string>;
  rowGap?: ResponsiveValue<string>;
  borderRadius?: string;
  activeMaterialId?: string;
  loadingMaterialId?: string;
  hideFavoriteButton?: boolean;
  groupByCollection?: boolean;
  overflow?: StackProps['overflow'];
}

export const FanPresentation: FC<Props> = ({
  observer,
  setActiveMaterial,
  materials,
  type,
  isLoading,
  disableHovering,
  itemHeight,
  rowGap = '3',
  borderRadius,
  activeMaterialId,
  loadingMaterialId,
  hideFavoriteButton = false,
  groupByCollection = false,
  overflow = 'auto',
}) => {
  const fanPages = materials.reduce<Record<string, Material[]>>((acc, curr) => {
    const key = groupByCollection
      ? `${curr.producer}|${curr.collection}|${curr.page}`
      : String(curr.page);
    (acc[key] ??= []).push(curr);
    return acc;
  }, {});

  const sortedKeys = Object.keys(fanPages).sort((a, b) => {
    const first = fanPages[a][0];
    const second = fanPages[b][0];
    return groupByCollection
      ? first.producer.localeCompare(second.producer) ||
          first.collection.localeCompare(second.collection) ||
          first.page - second.page
      : first.page - second.page;
  });

  const rows = sortedKeys.flatMap((key) => {
    const fanPage = fanPages[key];
    return fanPage[0].page > 0
      ? [{ key, fanPage }]
      : Array.from({ length: Math.ceil(fanPage.length / 6) }, (_, index) => ({
          key: `${key}-${index}`,
          fanPage: fanPage.slice(index * 6, index * 6 + 6),
        }));
  });

  return (
    <VStack gap={rowGap} overflow={overflow}>
      {rows.map(({ key, fanPage }, i) => (
        <FanPresentationRow
          key={key}
          fanPage={fanPage}
          showObserver={i === Math.max(0, rows.length - 5)}
          observer={observer}
          setActiveMaterial={setActiveMaterial}
          disableHovering={disableHovering}
          height={itemHeight}
          borderRadius={borderRadius}
          activeMaterialId={activeMaterialId}
          loadingMaterialId={loadingMaterialId}
          hideFavoriteButton={hideFavoriteButton}
        />
      ))}

      {isLoading &&
        repeat(20).map((i) => (
          <ListPresentationItem
            materialType={type}
            key={i}
            hideFavoriteButton={hideFavoriteButton}
          />
        ))}
    </VStack>
  );
};
